import { useState, useEffect, useCallback } from "react";
import { useAudit } from "../context/AuditContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import API from "../lib/api";
import { UserPlus, Users } from "lucide-react";

export default function AuditAssignment() {
  const { auditRun } = useAudit();
  const { hasPermission } = useAuth();
  const [allUsers, setAllUsers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const canAssign = hasPermission("run:assign");

  const fetchData = useCallback(async () => {
    if (!auditRun?.id || !canAssign) return;
    try {
      const [usersRes, assignedRes] = await Promise.all([
        API.get("/users"),
        API.get(`/audit-runs/${auditRun.id}/assigned-users`)
      ]);
      setAllUsers(usersRes.data.filter(u => u.status === "active"));
      setAssignments(
        assignedRes.data.map(u => ({
          user_id: u.id,
          access_expires_at: u.assignment?.access_expires_at || "",
        }))
      );
    } catch (e) { /* ignore */ }
  }, [auditRun?.id, canAssign]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleUser = (userId) => {
    setAssignments(prev =>
      prev.some(item => item.user_id === userId)
        ? prev.filter(item => item.user_id !== userId)
        : [...prev, { user_id: userId, access_expires_at: "" }]
    );
  };

  const updateExpiry = (userId, access_expires_at) => {
    setAssignments(prev => prev.map(item => (
      item.user_id === userId ? { ...item, access_expires_at } : item
    )));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await API.post(`/audit-runs/${auditRun.id}/assign`, {
        assignments: assignments.map(item => ({
          user_id: item.user_id,
          ...(item.access_expires_at ? { access_expires_at: item.access_expires_at } : {}),
        })),
      });
      toast.success("User assignments saved");
      setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save assignments"); }
    setSaving(false);
  };

  if (!canAssign || !auditRun) return null;

  const assignedUsers = allUsers.filter(u => assignments.some(item => item.user_id === u.id));
  const roleLabel = (role) => ({
    super_admin: "Super Admin",
    audit_manager: "Audit Manager",
    senior_auditor: "Senior Auditor",
    auditor: "Auditor",
    external_auditor: "External Auditor",
    control_owner: "Control Owner",
    observer: "Observer",
  }[role] || role);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-9 px-3 rounded-xl border-border bg-card hover:bg-accent text-xs font-semibold gap-1.5 shrink-0"
          data-testid="manage-assignment-btn"
          onClick={fetchData}
          title={`Assign Users (${assignments.length})`}
        >
          <UserPlus className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
          <span className="hidden sm:inline">Assign ({assignments.length})</span>
          <span className="sm:hidden font-mono text-[11px]">({assignments.length})</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Assign Users to Audit</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">Select users who should have access to this audit run.</p>
          <div className="max-h-60 overflow-y-auto space-y-2">
            {allUsers.map(u => (
              <div key={u.id} className="flex items-center gap-3 p-2 border rounded-md hover:bg-accent/50 transition-colors">
                <Checkbox
                  checked={assignments.some(item => item.user_id === u.id)}
                  onCheckedChange={() => toggleUser(u.id)}
                  data-testid={`assign-user-${u.id}`}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{u.name}</p>
                  <p className="text-xs text-muted-foreground font-mono truncate">{u.email}</p>
                </div>
                <Badge variant="outline" className="text-[10px] shrink-0">
                  {roleLabel(u.role)}
                </Badge>
              </div>
            ))}
            {allUsers.map(u => {
              const assignment = assignments.find(item => item.user_id === u.id);
              if (!assignment || u.role !== "external_auditor") return null;
              return (
                <div key={`${u.id}-expiry`} className="pl-9 pr-2">
                  <input
                    type="datetime-local"
                    value={assignment.access_expires_at || ""}
                    onChange={(e) => updateExpiry(u.id, e.target.value)}
                    className="w-full rounded-md border bg-background px-3 py-1.5 text-xs"
                  />
                </div>
              );
            })}
            {allUsers.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">No users available</p>
            )}
          </div>
          <Button onClick={handleSave} disabled={saving} className="w-full" data-testid="save-assignments-btn">
            Save Assignments
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
